// src/components/navigation/AppNavigator.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { View, ActivityIndicator } from "react-native";
import { NavigationContainer } from "@react-navigation/native";
import messaging from "@react-native-firebase/messaging";

import { AuthStack } from "../navigation/AuthStack";
import { AppStack } from "../navigation/AppStack";

import { Storage } from "../services/storage/storage";
import { StorageKeys } from "../services/storage/keys";
import { useAuth } from "../../context/AuthContext";
import { AuthStackParamList } from "./navigationTypes";
import { navigationRef } from "./navigationRef";
import { isValidHistoryDate } from "../shared/utils/historyNavigation";

const styles = {
  loadingWrap: { flex: 1, justifyContent: "center", alignItems: "center" } as const,
};

export default function AppNavigator() {
  const { user, isHydrating } = useAuth();

  const [isReady, setIsReady] = useState(false);
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(false);
  const [pendingNotificationRoute, setPendingNotificationRoute] = useState<{
    name: keyof AuthStackParamList;
    params?: AuthStackParamList[keyof AuthStackParamList];
  } | null>(null);
  const didCheckInitialNotification = useRef(false);

  useEffect(() => {
    (async () => {
      const v = await Storage.get(StorageKeys.hasSeenOnboarding);
      setHasSeenOnboarding(v === "true");
      setIsReady(true);
    })();
  }, []);

  const handleNotificationNavigation = useCallback((message?: messaging.RemoteMessage | null) => {
    if (!user) {
      return;
    }

    const requestedScreen = String(message?.data?.screen ?? "").trim().toLowerCase();
    const requestedDate = String(message?.data?.date ?? "").trim();
    const notificationDate = isValidHistoryDate(requestedDate)
      ? requestedDate
      : undefined;

    const nextRoute: {
      name: keyof AuthStackParamList;
      params?: AuthStackParamList[keyof AuthStackParamList];
    } =
      requestedScreen === "score"
        ? {
            name: "SadhanaTracker",
            params: {
              initialTab: "track",
              notificationDate,
            },
          }
        :
      requestedScreen === "adminconsole" && user.userType === "Guru"
        ? {
            name: "AdminConsole",
          }
        : requestedScreen === "divinecare"
        ? {
            name: "SadhanaTracker",
            params: {
              initialTab: "divine",
            },
          }
        : user.userType === "Devotee"
          ? {
              name: "SadhanaTracker",
              params: {
                initialTab: "divine",
              },
            }
          : {
              name: "SadhanaTracker",
            };

    if (navigationRef.isReady()) {
      navigationRef.navigate(nextRoute.name, nextRoute.params);
      setPendingNotificationRoute(null);
      return;
    }

    setPendingNotificationRoute(nextRoute);
  }, [user]);

  useEffect(() => {
    if (!user) {
      setPendingNotificationRoute(null);
    }
  }, [user]);

  useEffect(() => {
    const unsubscribe = messaging().onNotificationOpenedApp((message) => {
      handleNotificationNavigation(message);
    });

    return unsubscribe;
  }, [handleNotificationNavigation]);

  useEffect(() => {
    if (didCheckInitialNotification.current) {
      return;
    }

    didCheckInitialNotification.current = true;

    messaging()
      .getInitialNotification()
      .then((message) => {
        if (message) {
          handleNotificationNavigation(message);
        }
      })
      .catch((error) => {
        console.log("Initial notification handling failed:", error);
      });
  }, [handleNotificationNavigation]);

  if (!isReady || isHydrating) {
    return (
      <View style={styles.loadingWrap}>
        <ActivityIndicator />
      </View>
    );
  }

  return (
    <NavigationContainer
      ref={navigationRef}
      onReady={() => {
        if (user && pendingNotificationRoute) {
          navigationRef.navigate(
            pendingNotificationRoute.name,
            pendingNotificationRoute.params
          );
          setPendingNotificationRoute(null);
        }
      }}
    >
      {user ? <AppStack /> : <AuthStack hasSeenOnboarding={hasSeenOnboarding} />}
    </NavigationContainer>
  );
}
